import React, { useState, useEffect } from 'react';
import type { CatalogResponse, DocCatalogEntry } from '../../services/docsService';
import { useTheme } from '../../contexts/ThemeContext';
import { DocsGroupSection } from './DocsGroupSection';

interface Props {
  catalog: CatalogResponse;
  currentSlug: string;
  activeAnchor: string | null;        // NEW — drives in-doc TOC accent
  autoExpandGroup?: string;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const DEPTH_KEY = 'docs_toc_depth';
const DEFAULT_DEPTH = '2';

// Wrap localStorage access — private-mode browsers / restrictive iframe
// contexts can throw on access. Falling back to default keeps the sidebar
// mountable; the operator just loses persistence in that session.
function readDepth(): string {
  try {
    return localStorage.getItem(DEPTH_KEY) ?? DEFAULT_DEPTH;
  } catch {
    return DEFAULT_DEPTH;
  }
}

function writeDepth(value: string): void {
  try {
    localStorage.setItem(DEPTH_KEY, value);
  } catch {
    /* private-mode storage write blocked — fail open */
  }
}

export const DocsSidebar: React.FC<Props> = ({
  catalog, currentSlug, activeAnchor, autoExpandGroup, mobileOpen, onMobileClose,
}) => {
  const { darkMode } = useTheme();
  const [tocDepth, setTocDepth] = useState<string>(readDepth);

  useEffect(() => {
    writeDepth(tocDepth);
  }, [tocDepth]);

  const numericDepth = tocDepth === 'all' ? Infinity : parseInt(tocDepth, 10);

  const docsByGroup = new Map<string, DocCatalogEntry[]>();
  for (const d of catalog.docs) {
    if (!docsByGroup.has(d.group)) docsByGroup.set(d.group, []);
    docsByGroup.get(d.group)!.push(d);
  }

  return (
    <>
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-30"
          onClick={onMobileClose}
        />
      )}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40 w-72
          ${darkMode ? 'bg-gray-900' : 'bg-white'}
          lg:bg-transparent
          ${darkMode ? 'border-gray-700' : 'border-gray-200'}
          border-r
          px-4 py-6 overflow-y-auto
          transform transition-transform
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex items-center justify-end mb-3">
          <label
            className={`flex items-center gap-1 text-xs ${
              darkMode ? 'text-gray-500' : 'text-gray-500'
            }`}
          >
            TOC depth
            <select
              aria-label="TOC depth"
              value={tocDepth}
              onChange={(e) => setTocDepth(e.target.value)}
              className={`text-xs rounded border px-1 py-0.5 ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-gray-300'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="all">All</option>
            </select>
          </label>
        </div>
        {catalog.groups.map((g) => {
          const docs = docsByGroup.get(g) || [];
          if (docs.length === 0) return null;
          const first = docs[0];
          return (
            <DocsGroupSection
              key={g}
              group={g}
              docs={docs}
              initialCollapsed={first.sidebar_collapsed}
              recentLimit={first.sidebar_recent_limit}
              currentSlug={currentSlug}
              activeAnchor={activeAnchor}
              tocDepth={numericDepth}
              autoExpand={autoExpandGroup === g}
              onLinkClick={onMobileClose}
              darkMode={darkMode}
            />
          );
        })}
      </aside>
    </>
  );
};
